// src/services/apiClient.ts
import { API_CONFIG } from "../utils/constants";

interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

interface ApiError extends Error {
  status?: number;
  errors?: Record<string, string[]>;
  response?: any;
}

/**
 * Pulls the most useful message out of an error thrown by this fetch-based
 * client (`error.response.detail`) or a raw axios call (`error.response.data.detail`).
 */
export const getErrorMessage = (error: any, fallback = "Something went wrong"): string => {
  const detail = error?.response?.data?.detail ?? error?.response?.detail;

  if (Array.isArray(detail)) {
    // FastAPI validation errors come back as a list of { msg, loc }.
    return detail.map((d: any) => d.msg || d).join(", ");
  }

  return (
    detail ||
    error?.response?.data?.message ||
    error?.response?.message ||
    error?.message ||
    fallback
  );
};

/**
 * The sender-update flow (Change Sender, Test Send) drives a real Salesforce
 * browser session that saves the new sender email, then polls up to ~90s for
 * someone to click the confirmation link Salesforce emails to that address.
 * That routinely exceeds a reverse proxy's read timeout (nginx defaults to
 * 60s), producing a raw 502/503/504 with no useful body — this turns that
 * into the actual explanation instead of "Gateway Time-out". Only call this
 * for errors from those two endpoints; a 504 elsewhere doesn't mean this.
 */
export const getSenderUpdateErrorMessage = (error: any, fallback = "Something went wrong"): string => {
  if ([502, 503, 504].includes(error?.status)) {
    return (
      "This is taking longer than expected — Salesforce is still waiting for the " +
      "confirmation link to be clicked. Open the sender's inbox and click that link, " +
      "then wait a couple of minutes before trying again."
    );
  }

  return getErrorMessage(error, fallback);
};

/**
 * Session is invalid/expired: clear stored auth and send the user back to sign in.
 * Shared so any client (this fetch-based one, or a raw axios call) can reuse it.
 */
export const handleUnauthorized = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');

  if (window.location.pathname !== '/signin') {
    window.location.href = '/signin';
  }
};

class ApiClient {
  private baseURL: string;
  private timeout: number;
  private headers: Record<string, string>;

  constructor(baseURL?: string, timeout: number = 1000000) {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = timeout;
    this.headers = {
      'Content-Type': 'application/json',
      // 'Content-Type': 'multipart/form-data',
    };
  }

  /**
   * Set Authorization token manually
   */
  setAuthToken(token: string) {
    this.headers['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove Authorization token
   */
  removeAuthToken() {
    delete this.headers['Authorization'];
  }

  /**
   * Attach token from localStorage automatically
   */
  private attachToken() {
    const token = localStorage.getItem('accessToken');
    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.headers['Authorization'];
    }
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): never {
    const apiError: ApiError = new Error(error.message || "An unknown error occurred") as ApiError;
    apiError.status = error.status;
    apiError.errors = error.errors;
    apiError.response = error.response;
    throw apiError;
  }

  /**
   * Session is invalid/expired: clear stored auth and send the user back to sign in.
   */
  private handleUnauthorized() {
    this.removeAuthToken();
    handleUnauthorized();
  }

  /**
   * Generic request
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    this.attachToken(); // attach token for every request
    const url = `${this.baseURL}${endpoint}`;

    const config: RequestInit = {
      ...options,
      headers: {
        ...this.headers,
        ...options.headers,
      },
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, { ...config, signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 401) {
          this.handleUnauthorized();
        }

        throw { message: response.statusText, status: response.status, response: errorData };
      }

      const data = await response.json();
      return { data, status: response.status, message: data.message };
    } catch (err: any) {
      this.handleError(err);
    }
  }

  /** GET request */
  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const queryString = params
      ? '?' + new URLSearchParams(params as any).toString()
      : '';
    return this.request<T>(`${endpoint}${queryString}`, { method: 'GET' });
  }

  /** POST request */
  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body: JSON.stringify(data) });
  } 

  /** PUT request */
  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body: JSON.stringify(data) });
  }

  /** PATCH request */
  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(data) });
  }

  /** DELETE request */
  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Singleton instance
export const apiClient = new ApiClient();
export default apiClient;
